import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Seo } from '../../components/Seo';
import { CharityPromo } from '../../components/CharityPromo';
import { MapPin, Gavel, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const FindAuctioneerHub = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/directory/overview`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setData({ states: [], total_auctioneers: 0, total_houses: 0 }))
      .finally(() => setLoading(false));
  }, []);

  const totalAuc = data?.total_auctioneers || 0;
  const totalHouse = data?.total_houses || 0;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://netauctionsinc.com/' },
        { '@type': 'ListItem', position: 2, name: 'Find an Auctioneer', item: 'https://netauctionsinc.com/find-an-auctioneer' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'How do I find a licensed auctioneer near me?', acceptedAnswer: { '@type': 'Answer', text: 'Browse the Net Auctions directory by state and city to find licensed, verified auctioneers and auction companies near you. Filter by specialty such as estate sales, real estate, farm equipment, and more.' } },
        { '@type': 'Question', name: 'Is the auctioneer directory free to use?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Searching and contacting auctioneers on Net Auctions is completely free for the public.' } },
      ],
    },
  ];

  return (
    <>
      <Navbar />
      <Seo
        title="Find an Auctioneer — Licensed Auctioneers & Auction Companies in All 50 States | Net Auctions"
        description={`Browse ${totalAuc.toLocaleString()} licensed auctioneers and ${totalHouse.toLocaleString()} auction companies across the United States. Find verified professionals for estate sales, real estate, farm equipment and more — free to search.`}
        canonicalPath="/find-an-auctioneer"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-gray-50" data-testid="find-auctioneer-hub">
        <div className="bg-gradient-to-r from-green-700 to-green-800 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Find an Auctioneer in the United States
            </h1>
            <p className="text-lg text-green-100 max-w-3xl mx-auto">
              Browse {totalAuc.toLocaleString()} licensed auctioneers and {totalHouse.toLocaleString()} auction
              companies across all 50 states. Free to search — no sign-up required.
            </p>
            <Link
              to="/find-auctioneer"
              data-testid="hub-search-cta"
              className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-white text-green-800 font-semibold rounded-full hover:bg-green-50 transition-colors"
            >
              <Search className="w-5 h-5" /> Search by ZIP code
            </Link>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-6">Browse Auctioneers by State</h2>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading states…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="state-grid">
              {data.states.map((s) => (
                <Link
                  key={s.state}
                  to={`/find-an-auctioneer/${s.state_slug}`}
                  data-testid={`state-card-${s.state_slug}`}
                  className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-green-500 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-gray-900 font-semibold group-hover:text-green-700">
                        <MapPin className="w-4 h-4 text-green-600" /> {s.state_name}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {s.auctioneer_count.toLocaleString()} auctioneers · {s.house_count.toLocaleString()} companies
                      </p>
                    </div>
                    <Gavel className="w-6 h-6 text-gray-300 group-hover:text-green-500" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <CharityPromo />
        </div>
      </div>
      <Footer />
    </>
  );
};
